import React from 'react';
import ShipmentBanner from 'src/components/ShipmentBanner';
import StatusBadge from 'src/components/StatusBadge';
import { Link } from 'straightline-ui';
import {
  Col,
  Icon,
  ListGroup,
  ListGroupItem,
  Row,
  Table,
  TableBody,
  TableData,
  TableHead,
  TableHeader,
  TableRow,
  UncontrolledTooltip
} from 'straightline-ui';
import { formatMoney, formatNumber } from 'straightline-utils/accounting';
import { isCancelled, truckType } from 'straightline-utils/constants';
import { formatDate } from 'straightline-utils/time';

export const ShipmentUnits = ({ shipment }) => {
  const units = shipment.line_items.reduce((units, item) => {
    return units + item.units;
  }, 0);
  return (
    <div>
      <div className="d-flex align-items-center">
        <Icon type="compass" className="text-secondary mr-1" />
        <span className="mr-3">{shipment.mode}</span>
        <Icon type="truck" className="text-secondary mr-1" />
        <span className="mr-3">{truckType(shipment.truck_type).label}</span>
        <Icon type="list" className="text-secondary mr-1" />
        <span className="mr-3">
          {formatNumber(shipment.accessorial_codes?.length || 0)} accs
        </span>
      </div>
      <div className="d-flex align-items-center">
        <Icon type="inbox" className="text-secondary mr-1" />
        <span className="mr-3">{formatNumber(units)} units</span>
        <Icon type="anchor" className="text-secondary mr-1" />
        <span className="mr-3">{formatNumber(shipment.load_weight)} lbs</span>
        <Icon type="map" className="text-secondary mr-1" />
        <span className="mr-3">{formatNumber(shipment.travel.miles)} mi</span>
      </div>
    </div>
  );
};

export const ShipmentCost = ({ shipment }) => {
  return (
    <div>
      <div className="d-flex align-items-center">
        <Icon type="truck" className="text-secondary mr-1" />
        <span className="mr-3">
          {formatMoney(shipment.rate.price.shipper_price)}
        </span>
        <Icon type="map" className="text-secondary mr-1" />
        <span className="mr-3">
          {formatMoney(
            shipment.rate.price.shipper_price /
              Math.max(shipment.travel.miles, 1)
          )}
          /mi
        </span>
        <Icon type="anchor" className="text-secondary mr-1" />
        <span className="mr-3">
          {formatMoney(
            shipment.rate.price.shipper_price / shipment.load_weight
          )}
          /lb
        </span>
      </div>
    </div>
  );
};

function truncateText(text) {
  if (!text) {
    return '';
  }

  if (text.length <= 20) {
    return text;
  }

  return text.substr(0, 20) + '...';
}

export const ShipmentsTable = ({ data, isLoading }) => {
  if (isLoading) {
    return <ShipmentsTableLoader />;
  }
  return (
    <div className="table-responsive">
      <Table className="table table-nowrap card-table table-hover">
        <TableHead>
          <TableRow>
            <TableHeader className="py-3">Status</TableHeader>
            <TableHeader className="py-3">ID</TableHeader>
            <TableHeader className="py-3">Pickup</TableHeader>
            <TableHeader className="py-3">Delivery</TableHeader>
            <TableHeader className="py-3">Carrier</TableHeader>
            <TableHeader className="py-3">Details</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody className="list">
          {data.map((shipment) => (
            <TableRow key={shipment.id} to={`/shipments/${shipment.id}`}>
              <TableData style={{ width: '1%' }}>
                <StatusBadge status={shipment.status} />
              </TableData>
              <TableData>
                <div>
                  <span className="text-muted">SL#: </span>
                  {shipment.id}
                </div>
                <div>
                  <span className="text-muted">REF#: </span>
                  {truncateText(shipment.reference_number) || (
                    <span className="text-muted">NA</span>
                  )}
                </div>
              </TableData>
              <TableData className="" style={{ width: '1%' }}>
                <div>
                  {`${shipment.pickup_address.city}, ${shipment.pickup_address.state}`}
                  {shipment.pickup_address.name && (
                    <span id={`pickup-name-${shipment.id}`} className="ml-1">
                      <Icon type="info" className="text-secondary" />
                      <UncontrolledTooltip
                        target={`pickup-name-${shipment.id}`}
                      >
                        {shipment.pickup_address.name}
                      </UncontrolledTooltip>
                    </span>
                  )}
                </div>
                <div>{formatDate(shipment.pickup_date, 'LLL do, yyyy')}</div>
              </TableData>
              <TableData
                className=""
                style={{ width: '1%', position: 'relative' }}
              >
                <div style={{ position: 'absolute', left: -12, top: 21 }}>
                  <Icon
                    type="arrow-right text-success"
                    style={{ fontSize: 25 }}
                  />
                </div>
                <div>
                  {`${shipment.delivery_address.city}, ${shipment.delivery_address.state}`}
                  {shipment.delivery_address.name && (
                    <span id={`delivery-name-${shipment.id}`} className="ml-1">
                      <Icon type="info" className="text-secondary" />
                      <UncontrolledTooltip
                        target={`delivery-name-${shipment.id}`}
                      >
                        {shipment.delivery_address.name}
                      </UncontrolledTooltip>
                    </span>
                  )}
                </div>
                <div>{formatDate(shipment.due_date, 'LLL do, yyyy')}</div>
              </TableData>
              <TableData>
                <div>
                  {(shipment.carrier && shipment.carrier.name) || (
                    <span className="text-muted">
                      {isCancelled(shipment) ? 'NA' : 'Looking for match...'}
                    </span>
                  )}
                </div>
                <ShipmentCost shipment={shipment} />
              </TableData>
              <TableData>
                <ShipmentUnits shipment={shipment} />
              </TableData>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export const ShipmentsList = ({ data, isLoading }) => {
  if (isLoading) {
    return <ShipmentsListLoader />;
  }
  return (
    <ListGroup className="list-group-flush">
      {data.map((shipment) => {
        return (
          <ListGroupItem
            key={shipment.id}
            tag={Link}
            to={`/shipments/${shipment.id}`}
            className="list-group-item-action px-3"
          >
            <ShipmentBanner shipment={shipment} />
            <ListGroup className="list-group list-group-flush list-group-activity">
              <ListGroupItem className="py-3">
                <Row>
                  <Col xs="auto">
                    <div className="avatar avatar-sm">
                      <div className="avatar-title font-size-lg rounded-circle bg-light text-dark">
                        <Icon type="clipboard" />
                      </div>
                    </div>
                  </Col>
                  <Col className="ml-n2">
                    <div className="font-weight-bold">
                      {shipment.carrier?.name || (
                        <span className="text-secondary">
                          Looking for match...
                        </span>
                      )}
                    </div>
                    <div className="mb-3">
                      {shipment.rate.service_level.name}
                    </div>
                    <div>
                      <span className="text-secondary">SL#: </span>
                      {shipment.id}
                    </div>
                    {shipment.reference_number && (
                      <div>
                        <span className="text-secondary">REF#: </span>
                        {shipment.reference_number}
                      </div>
                    )}
                    <StatusBadge status={shipment.status} />
                  </Col>
                </Row>
              </ListGroupItem>
              <ListGroupItem className="py-3">
                <Row>
                  <Col xs="auto">
                    <div className="avatar avatar-sm">
                      <div className="avatar-title font-size-lg rounded-circle bg-light text-dark">
                        <Icon type="truck" />
                      </div>
                    </div>
                  </Col>
                  <Col className="ml-n2">
                    {shipment.pickup_address.name && (
                      <div className="font-weight-bold">
                        {shipment.pickup_address.name}
                      </div>
                    )}
                    <div
                      className={
                        shipment.pickup_address.name ? '' : 'font-weight-bold'
                      }
                    >
                      {`${shipment.pickup_address.city}, ${shipment.pickup_address.state}`}
                    </div>
                    <div>{formatDate(shipment.pickup_date, 'LLL d, yyyy')}</div>
                  </Col>
                </Row>
              </ListGroupItem>
              <ListGroupItem className="pt-3 pb-0">
                <Row>
                  <Col xs="auto">
                    <div className="avatar avatar-sm">
                      <div className="avatar-title font-size-lg rounded-circle bg-light text-dark">
                        <Icon type="map-pin" />
                      </div>
                    </div>
                  </Col>
                  <Col className="ml-n2">
                    {shipment.delivery_address.name && (
                      <div className="font-weight-bold">
                        {shipment.delivery_address.name}
                      </div>
                    )}
                    <div
                      className={
                        shipment.delivery_address.name ? '' : 'font-weight-bold'
                      }
                    >
                      {`${shipment.delivery_address.city}, ${shipment.delivery_address.state}`}
                    </div>
                    <div>{formatDate(shipment.due_date, 'LLL d, yyyy')}</div>
                  </Col>
                </Row>
              </ListGroupItem>
            </ListGroup>
          </ListGroupItem>
        );
      })}
    </ListGroup>
  );
};

export const ShipmentsTableLoader = ({ length = 10 }) => {
  const loaderLength = new Array(length).fill(null);
  return (
    <div className="table-responsive">
      <Table className="table table-nowrap card-table">
        <TableHead>
          <TableRow>
            <TableHeader className="py-3">Status</TableHeader>
            <TableHeader className="py-3">ID</TableHeader>
            <TableHeader className="py-3">Pickup</TableHeader>
            <TableHeader className="py-3">
              <p className="ml-4 mb-0">Delivery</p>
            </TableHeader>
            <TableHeader className="py-3">Carrier</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody className="list">
          {loaderLength.map((item, index) => (
            <TableRow style={{ height: 79 }} key={index}>
              <TableData style={{ width: '1%' }}>
                <div className="d-flex">
                  <div
                    className="bg-light rounded mr-2"
                    style={{ height: 29, width: 42 }}
                  />
                  <div
                    className="bg-light rounded"
                    style={{ height: 29, width: 78 }}
                  />
                </div>
              </TableData>
              <TableData>
                <div className="d-flex justify-content-between">
                  <div
                    className="bg-light rounded mb-2"
                    style={{ height: 18, width: 40 }}
                  />
                  <div
                    className="bg-light rounded mb-2"
                    style={{ height: 18, width: 20 }}
                  />
                </div>
                <div
                  className="bg-light rounded"
                  style={{ height: 18, width: 65 }}
                />
              </TableData>
              <TableData className="pr-0" style={{ width: '1%' }}>
                <div
                  className="bg-light rounded mb-2"
                  style={{ height: 18, width: 92 }}
                />
                <div
                  className="bg-light rounded"
                  style={{ height: 18, width: 89 }}
                />
              </TableData>
              <TableData className="pl-0" style={{ width: '1%' }}>
                <div className="d-inline-block mx-2">
                  <Icon
                    type="arrow-right"
                    className="text-light"
                    style={{ fontSize: 25 }}
                  />
                </div>
                <div className="d-inline-block" style={{ marginBottom: -7 }}>
                  <div
                    className="bg-light rounded mb-2"
                    style={{ height: 18, width: 92 }}
                  />
                  <div
                    className="bg-light rounded"
                    style={{ height: 18, width: 89 }}
                  />
                </div>
              </TableData>
              <TableData>
                <div
                  className="bg-light rounded mb-2"
                  style={{ height: 18, width: 82 }}
                />
                <div
                  className="bg-light rounded"
                  style={{ height: 18, width: 100 }}
                />
              </TableData>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export const ShipmentsListLoader = ({ length = 10 }) => {
  const loaderLength = new Array(length).fill(null);
  return (
    <ListGroup className="list-group-flush mb-0">
      {loaderLength.map((item, index) => {
        return (
          <ListGroupItem key={index} className="px-4">
            <div
            // className="animated flash infinite slower"
            >
              <Row className="justify-content-between mb-3">
                <Col xs="auto">
                  <div
                    className="bg-light rounded"
                    style={{ height: 33, width: 100 }}
                  />
                </Col>
                <Col xs="auto">
                  <div className="text-secondary d-flex align-items-center">
                    <div
                      className="bg-light rounded"
                      style={{ height: 14, width: 40 }}
                    />
                    <Icon type="dot" className="text-muted mx-1" />
                    <div
                      className="bg-light rounded"
                      style={{ height: 14, width: 60 }}
                    />
                  </div>
                  <div className="text-secondary d-flex align-items-center">
                    <div
                      className="bg-light rounded"
                      style={{ height: 14, width: 60 }}
                    />
                    <Icon type="dot" className="text-muted mx-1" />
                    <div
                      className="bg-light rounded"
                      style={{ height: 14, width: 40 }}
                    />
                  </div>
                </Col>
              </Row>
              <ListGroup className="list-group list-group-flush list-group-activity">
                <ListGroupItem className="py-3">
                  <Row>
                    <Col xs="auto">
                      <div className="avatar avatar-sm">
                        <div className="avatar-title font-size-lg rounded-circle bg-light text-dark">
                          <Icon type="clipboard" />
                        </div>
                      </div>
                    </Col>
                    <Col className="ml-n2">
                      <div
                        className="bg-light rounded mb-1"
                        style={{ height: 14, width: 120 }}
                      />
                      <div
                        className="bg-light rounded mb-1"
                        style={{ height: 14, width: 100 }}
                      />
                      <div
                        className="bg-light rounded mb-1"
                        style={{ height: 14, width: 190 }}
                      />
                      <div
                        className="bg-light rounded mb-1"
                        style={{ height: 14, width: 75 }}
                      />
                    </Col>
                  </Row>
                </ListGroupItem>

                <ListGroupItem className="py-3">
                  <Row>
                    <Col xs="auto">
                      <div className="avatar avatar-sm">
                        <div className="avatar-title font-size-lg rounded-circle bg-light text-dark">
                          <Icon type="truck" />
                        </div>
                      </div>
                    </Col>
                    <Col className="ml-n2">
                      <div>
                        <div
                          className="bg-light rounded mb-2"
                          style={{ height: 18, width: 160 }}
                        />
                        <div>
                          <div
                            className="bg-light rounded"
                            style={{ height: 16, width: 140 }}
                          />
                        </div>
                      </div>
                    </Col>
                  </Row>
                </ListGroupItem>
                <ListGroupItem className="pt-3 pb-0">
                  <Row>
                    <Col xs="auto">
                      <div className="avatar avatar-sm">
                        <div className="avatar-title font-size-lg rounded-circle bg-light text-dark">
                          <Icon type="map-pin" />
                        </div>
                      </div>
                    </Col>
                    <Col className="ml-n2">
                      <div>
                        <div
                          className="bg-light rounded mb-2"
                          style={{ height: 18, width: 170 }}
                        />
                        <div
                          className="bg-light rounded"
                          style={{ height: 18, width: 170 }}
                        />
                      </div>
                    </Col>
                  </Row>
                </ListGroupItem>
              </ListGroup>
            </div>
          </ListGroupItem>
        );
      })}
    </ListGroup>
  );
};
